import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useClient } from '../../context/ClientContext';

const INTENT_COLORS = {
  'Branded': '#2563eb',
  'High Intent': '#10b981',
  'Informational': '#f59e0b',
  'Other': '#9ca3af'
};

export default function SearchTermIntent({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchSearchTermIntent(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data?.categories?.length) return null;

  const totalTerms = data.categories.reduce((s, c) => s + c.count, 0);
  const currency = data.currency || '$';

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Search Term Intent
      </h3>
      <p className="text-xs text-gray-400 mb-4">Top 200 search terms classified by intent</p>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-shrink-0">
          <ResponsiveContainer width={180} height={180}>
            <PieChart>
              <Pie
                data={data.categories}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
              >
                {data.categories.map((c) => (
                  <Cell key={c.name} fill={INTENT_COLORS[c.name] || '#9ca3af'} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [`${value} terms (${totalTerms > 0 ? ((value / totalTerms) * 100).toFixed(0) : 0}%)`, name]}
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 space-y-3">
          {data.categories.map((cat) => (
            <div key={cat.name}>
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: INTENT_COLORS[cat.name] || '#9ca3af' }}
                />
                <span className="text-sm font-medium text-gray-900">{cat.name}</span>
                <span className="text-xs text-gray-400">
                  {cat.count} terms · {cat.conversions} conv. · {currency}{cat.cost.toFixed(0)}
                </span>
              </div>
              {cat.topTerms?.length > 0 && (
                <p className="text-xs text-gray-500 ml-4.5 pl-0.5">
                  {cat.topTerms.map((t, i) => (
                    <span key={i}>
                      {i > 0 && <span className="text-gray-300 mx-1">·</span>}
                      <span className="italic">{t}</span>
                    </span>
                  ))}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
